import { Fragment, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AGENCY_FIELD_KIND_DEFS,
  AGENCY_FIELD_KIND_MAP,
  AGENCY_PROJECT_KIND_MAP,
  PRIORITY_MAP,
  PROJECT_HEALTH_DEFS,
  PROJECT_COLORS,
  type AgencyFieldKind,
  type AgencyFieldOption,
  type AgencyFieldView,
  type AgencyProjectDetail,
  type AgencySectionView,
  type AgencyTaskView,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { date } from '../../lib/format';
import { Icon, type IconName } from '../../components/Icon';
import { Badge, ErrorState, Modal, PageHead } from '../../components/ui';
import { Avatar, useTeamDirectory } from '../../components/People';
import { Menu } from '../../components/Menu';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { TaskDrawer } from './TaskDrawer';
import { ClientPreviewModal, ContractPreviewModal } from './Previews';
import { EntityRating } from '../../components/RatingRequest';

/**
 * لوحة المشروع — على نسق أسانا.
 *
 * الأقسام أعمدةٌ يسمّيها الفريق، والبطاقات تُسحب بينها وترتيبها يدويّ.
 * والسحب وحده يُقصي من يعمل باللمس أو لوحة المفاتيح، فقائمة البطاقة
 * تعرض «انقل إلى…» — الطريق نفسه لا بديلًا أدنى.
 */
export function AgencyProjectBoard() {
  const { id = '' } = useParams();
  /* `?from=account` — جاء من ملف العميل فيُمنح زرَّ عودةٍ إليه. */
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { atLeast } = useAuth();
  const { byId } = useTeamDirectory();

  /* `?task=` يفتح لوحَ المهمة فور الدخول — من جاء من سجلّ التعامل يجدها. */
  const [openTask, setOpenTask] = useState<string | null>(params.get('task'));
  const [previewClient, setPreviewClient] = useState(false);
  const [previewContract, setPreviewContract] = useState(false);
  const [editing, setEditing] = useState(false);
  const [managingFields, setManagingFields] = useState(false);
  /* عرض اللوحة أو القائمة — كأسانا، والاختيار يبقى للجلسة. */
  const [view, setView] = useState<'overview' | 'board' | 'list' | 'timeline' | 'calendar' | 'reports'>(
    () => (sessionStorage.getItem('goyone.agency.view') as 'overview' | 'board' | 'list' | 'timeline' | 'calendar' | 'reports') || 'board',
  );
  const [addingSection, setAddingSection] = useState(false);
  const [sectionName, setSectionName] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<string | null>(null);

  const switchView = (v: 'overview' | 'board' | 'list' | 'timeline' | 'calendar' | 'reports') => {
    setView(v);
    try {
      sessionStorage.setItem('goyone.agency.view', v);
    } catch {
      /* التخزين قد يُمنع — الاختيار يعمل للجلسة الحالية على الأقل. */
    }
  };

  const detail = useQuery({
    queryKey: ['agency', 'project', id],
    queryFn: () => api<AgencyProjectDetail>(`/agency/projects/${id}`),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['agency'] });

  const move = useMutation({
    mutationFn: ({ taskId, sectionId, index }: { taskId: string; sectionId: string; index: number }) =>
      api(`/agency/tasks/${taskId}/move`, { method: 'POST', body: { sectionId, index } }),
    onSuccess: refresh,
    onError: (e) => {
      toast(e instanceof Error ? e.message : 'تعذّر النقل.');
      refresh();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ taskId, completed }: { taskId: string; completed: boolean }) =>
      api(`/agency/tasks/${taskId}`, { method: 'PATCH', body: { completed } }),
    onSuccess: refresh,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const addSection = useMutation({
    mutationFn: (name: string) =>
      api(`/agency/projects/${id}/sections`, { method: 'POST', body: { name } }),
    onSuccess: () => {
      setSectionName('');
      setAddingSection(false);
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّرت الإضافة.'),
  });

  const removeSection = useMutation({
    mutationFn: (sectionId: string) => api(`/agency/sections/${sectionId}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  const renameSection = useMutation({
    mutationFn: ({ sectionId, name }: { sectionId: string; name: string }) =>
      api(`/agency/sections/${sectionId}`, { method: 'PATCH', body: { name } }),
    onSuccess: refresh,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const updateProject = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/agency/projects/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const removeProject = useMutation({
    mutationFn: () => api(`/agency/projects/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      navigate('/agency/projects');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  const project = detail.data;

  /* البطاقات مجمّعة بالقسم — واليتيمة (قسمها حُذف) في عمودٍ خاص. */
  const bySection = useMemo(() => {
    const map = new Map<string, AgencyTaskView[]>();
    for (const t of project?.tasks ?? []) {
      const key = t.sectionId ?? 'none';
      map.set(key, [...(map.get(key) ?? []), t]);
    }
    return map;
  }, [project?.tasks]);

  const doneCount = (project?.tasks ?? []).filter((t) => t.completed).length;
  const total = project?.tasks.length ?? 0;

  const dropOn = (sectionId: string, index: number) => {
    if (!dragId) return;
    setDropHint(null);
    move.mutate({ taskId: dragId, sectionId, index });
    setDragId(null);
  };

  return (
    <div className="page">
      {detail.isError && <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />}
      {detail.isPending && <div className="skeleton" style={{ height: 240 }} />}

      {project && (
        <>
          <PageHead
            title={project.name}
            avatar={<span className="ag-project-dot" style={{ background: project.color }} />}
            subtitle={
              total > 0
                ? `${doneCount}/${total} مهمة مكتملة`
                : 'لا مهام بعد — أضف أولى المهام في أي قسم'
            }
            actions={
              <>
                <Badge kind={AGENCY_PROJECT_KIND_MAP[project.kind]?.kind}>
                  {AGENCY_PROJECT_KIND_MAP[project.kind]?.label ?? project.kind}
                </Badge>
                {project.contractId && (
                  <button className="ag-ctx-chip" onClick={() => setPreviewContract(true)}>
                    <Icon name="clipboard" size={13} />
                    عقد {project.contractNumber ?? ''}
                  </button>
                )}
                {project.health && (
                  <Badge kind={PROJECT_HEALTH_DEFS.find((h) => h.key === project.health)?.kind}>
                    {PROJECT_HEALTH_DEFS.find((h) => h.key === project.health)?.label}
                  </Badge>
                )}
                {project.ownerId && byId.get(project.ownerId) && (
                  <span className="ag-ctx-chip" title="مالك المشروع">
                    <Avatar
                      name={byId.get(project.ownerId)!.name}
                      src={byId.get(project.ownerId)!.avatarUrl}
                      size={18}
                    />
                    {byId.get(project.ownerId)!.name}
                  </span>
                )}
                {project.accountId && (
                  <button className="btn btn-sm" onClick={() => setPreviewClient(true)}>
                    <Icon name="building" size={14} />
                    {project.accountName}
                  </button>
                )}

                {/*
                  من جاء من ملف عميلٍ يعود إليه بزرٍّ صريح.

                  والدربُ يأتي في الرابط (`backTo`) لا يُستنتج من مشروع
                  اللوح: مهامّ العملاء المحتملين تسكن مشروعًا داخليًّا بلا
                  عميل، فمن دخل من ملفٍ ما كان ليجد طريق العودة لو قِيس
                  بعميل المشروع. و`from=account` تبقى للروابط القديمة.
                */}
                {(() => {
                  const backTo =
                    params.get('backTo') ??
                    (params.get('from') === 'account' && project.accountId
                      ? `/crm/accounts/${project.accountId}`
                      : null);
                  /* لا يُقبل إلا مسارٌ داخليّ — رابطٌ خارجيّ في المَعلمة لا يُتبع. */
                  if (!backTo || !backTo.startsWith('/crm/')) return null;
                  return (
                    <Link to={backTo} className="btn btn-sm">
                      <Icon name="arrowLeft" size={15} />
                      ملف العميل
                    </Link>
                  );
                })()}
                <Link to="/agency/projects" className="btn btn-sm">
                  <Icon name="arrowLeft" size={15} />
                  المشاريع
                </Link>
                {atLeast('MANAGER') && (
                <Menu
                  actions={[
                    { key: 'edit', label: 'تعديل المشروع', icon: 'edit', onSelect: () => setEditing(true) },
                    {
                      key: 'fields',
                      label: 'حقول المشروع',
                      icon: 'filter',
                      onSelect: () => setManagingFields(true),
                    },
                    {
                      key: 'archive',
                      label: project.archived ? 'إلغاء الأرشفة' : 'أرشفة المشروع',
                      icon: 'inbox',
                      onSelect: () => updateProject.mutate({ archived: !project.archived }),
                    },
                    {
                      key: 'delete',
                      label: 'حذف المشروع',
                      icon: 'trash',
                      danger: true,
                      separated: true,
                      disabled: !atLeast('MANAGER'),
                      title: atLeast('MANAGER') ? undefined : 'الحذف للإدارة',
                      onSelect: () => {
                        if (window.confirm('حذف المشروع وكل مهامه؟ لا يمكن التراجع.')) {
                          removeProject.mutate();
                        }
                      },
                    },
                  ]}
                />
                )}
              </>
            }
          />

          {/* تبويبات المشروع — كأسانا */}
          <div className="ag-tabs" role="tablist">
            {(
              [
                ['overview', 'نظرة عامة', 'home'],
                ['list', 'قائمة', 'menu'],
                ['board', 'لوحة', 'layers'],
                ['timeline', 'خط زمني', 'gantt'],
                ['calendar', 'تقويم', 'calendar'],
                ['reports', 'لوحة القياس', 'chart'],
              ] as const
            ).map(([key, label, icon]) => (
              <button
                key={key}
                role="tab"
                aria-selected={view === key}
                className={`ag-tab${view === key ? ' is-active' : ''}`}
                onClick={() => switchView(key)}
              >
                <Icon name={icon} size={14} />
                {label}
              </button>
            ))}
          </div>

          {view === 'overview' ? (
            <OverviewTab
              project={project}
              byId={byId}
              canManage={atLeast('MANAGER')}
              onSave={(body) => updateProject.mutate(body)}
            />
          ) : view === 'timeline' ? (
            <TimelineTab project={project} onOpen={(taskId) => setOpenTask(taskId)} />
          ) : view === 'calendar' ? (
            <CalendarTab project={project} onOpen={(taskId) => setOpenTask(taskId)} />
          ) : view === 'reports' ? (
            <ProjectReports project={project} byId={byId} />
          ) : view === 'list' ? (
            <ListView
              project={project}
              bySection={bySection}
              byId={byId}
              canManage={atLeast('MANAGER')}
              onOpen={(taskId) => setOpenTask(taskId)}
              onToggle={(taskId, completed) => toggle.mutate({ taskId, completed })}
              onAdd={(sectionId, title) =>
                api(`/agency/tasks`, {
                  method: 'POST',
                  body: { projectId: id, sectionId, title },
                }).then(refresh, (e: unknown) =>
                  toast(e instanceof Error ? e.message : 'تعذّرت الإضافة.'),
                )
              }
            />
          ) : (
          <div className="ag-board">
            {/* عمود اليتيمات إن وُجدت */}
            {bySection.has('none') && (
              <BoardColumn
                key="none"
                section={{ id: 'none', name: 'بلا قسم', order: -1 }}
                tasks={bySection.get('none') ?? []}
                canDrop={false}
                {...columnHandlers()}
              />
            )}

            {project.sections.map((s, i) => (
              <BoardColumn
                key={s.id}
                section={s}
                num={i + 1}
                tasks={bySection.get(s.id) ?? []}
                canDrop
                {...columnHandlers(s.id)}
              />
            ))}

            {/* إضافة قسم — للإدارة */}
            {atLeast('MANAGER') && (
            <div className="ag-col ag-col-add">
              {addingSection ? (
                <input
                  className="input"
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && sectionName.trim()) addSection.mutate(sectionName.trim());
                    if (e.key === 'Escape') setAddingSection(false);
                  }}
                  onBlur={() => {
                    if (sectionName.trim()) addSection.mutate(sectionName.trim());
                    else setAddingSection(false);
                  }}
                  placeholder="اسم القسم"
                  autoFocus
                />
              ) : (
                <button className="ag-add-section" onClick={() => setAddingSection(true)}>
                  <Icon name="plus" size={15} />
                  قسم جديد
                </button>
              )}
            </div>
            )}
          </div>
          )}
        </>
      )}

      {openTask && <TaskDrawer taskId={openTask} onClose={() => setOpenTask(null)} />}

      {previewClient && project?.accountId && (
        <ClientPreviewModal accountId={project.accountId} onClose={() => setPreviewClient(false)} />
      )}
      {previewContract && project?.contractId && (
        <ContractPreviewModal contractId={project.contractId} onClose={() => setPreviewContract(false)} />
      )}

      {editing && project && (
        <EditProjectModal
          project={project}
          onClose={() => setEditing(false)}
          onSave={(body) => updateProject.mutate(body)}
        />
      )}

      {managingFields && project && (
        <FieldsModal
          projectId={project.id}
          fields={project.fieldDefs}
          onClose={() => setManagingFields(false)}
          onChanged={refresh}
        />
      )}
    </div>
  );

  /** معالجات العمود — مغلقة على حالة السحب المشتركة. */
  function columnHandlers(sectionId?: string) {
    return {
      canManage: atLeast('MANAGER'),
      sections: project?.sections ?? [],
      fieldDefs: project?.fieldDefs ?? [],
      byId,
      dragId,
      dropHint,
      onDragStart: (taskId: string) => setDragId(taskId),
      onDragEnd: () => {
        setDragId(null);
        setDropHint(null);
      },
      onHint: (key: string | null) => setDropHint(key),
      onDrop: (index: number) => sectionId && dropOn(sectionId, index),
      onOpen: (taskId: string) => setOpenTask(taskId),
      onToggle: (taskId: string, completed: boolean) => toggle.mutate({ taskId, completed }),
      onMoveTo: (taskId: string, target: string) =>
        move.mutate({ taskId, sectionId: target, index: 10_000 }),
      onAdd: (title: string) =>
        sectionId
          ? api(`/agency/tasks`, {
              method: 'POST',
              body: { projectId: id, sectionId, title },
            }).then(refresh, (e: unknown) =>
              toast(e instanceof Error ? e.message : 'تعذّرت الإضافة.'),
            )
          : Promise.resolve(),
      onRename: (secId: string, name: string) => renameSection.mutate({ sectionId: secId, name }),
      onRemove: (secId: string) => {
        if (window.confirm('حذف القسم؟ مهامه تبقى بلا قسم ولا تُحذف.')) {
          removeSection.mutate(secId);
        }
      },
    };
  }
}

/* ==========================================================================
   العمود
   ========================================================================== */

function BoardColumn({
  section,
  num,
  tasks,
  canDrop,
  canManage,
  sections,
  fieldDefs,
  byId,
  dragId,
  dropHint,
  onDragStart,
  onDragEnd,
  onHint,
  onDrop,
  onOpen,
  onToggle,
  onMoveTo,
  onAdd,
  onRename,
  onRemove,
}: {
  section: AgencySectionView;
  num?: number;
  tasks: AgencyTaskView[];
  canDrop: boolean;
  canManage: boolean;
  sections: AgencySectionView[];
  fieldDefs: AgencyFieldView[];
  byId: Map<string, { name: string; avatarUrl: string | null }>;
  dragId: string | null;
  dropHint: string | null;
  onDragStart: (taskId: string) => void;
  onDragEnd: () => void;
  onHint: (key: string | null) => void;
  onDrop: (index: number) => void;
  onOpen: (taskId: string) => void;
  onToggle: (taskId: string, completed: boolean) => void;
  onMoveTo: (taskId: string, sectionId: string) => void;
  onAdd: (title: string) => void;
  onRename: (sectionId: string, name: string) => void;
  onRemove: (sectionId: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(section.name);

  const real = section.id !== 'none';
  const hintKey = `col:${section.id}`;

  return (
    <div
      className={`ag-col${dropHint === hintKey ? ' is-over' : ''}`}
      onDragOver={(e) => {
        if (!canDrop || !dragId) return;
        e.preventDefault();
        onHint(hintKey);
      }}
      onDragLeave={() => dropHint === hintKey && onHint(null)}
      onDrop={(e) => {
        if (!canDrop || !dragId) return;
        e.preventDefault();
        onDrop(tasks.length);
      }}
    >
      <div className="ag-col-head">
        {renaming && real ? (
          <input
            className="input"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && nameDraft.trim()) {
                onRename(section.id, nameDraft.trim());
                setRenaming(false);
              }
              if (e.key === 'Escape') setRenaming(false);
            }}
            onBlur={() => {
              if (nameDraft.trim() && nameDraft.trim() !== section.name) {
                onRename(section.id, nameDraft.trim());
              }
              setRenaming(false);
            }}
            autoFocus
          />
        ) : (
          <>
            {num != null && <span className="ag-num num">{num}</span>}
            <strong className="ag-col-name">{section.name}</strong>
            <span className="ag-col-count num">{tasks.length}</span>
            {real && canManage && canManage && (
              <Menu
                actions={[
                  { key: 'rename', label: 'إعادة تسمية', icon: 'edit', onSelect: () => setRenaming(true) },
                  {
                    key: 'delete',
                    label: 'حذف القسم',
                    icon: 'trash',
                    danger: true,
                    onSelect: () => onRemove(section.id),
                  },
                ]}
              />
            )}
          </>
        )}
      </div>

      <div className="ag-col-body">
        {tasks.map((t, i) => {
          const overdue = !t.completed && t.dueAt && new Date(t.dueAt).getTime() < Date.now();
          const assignee = t.assigneeId ? byId.get(t.assigneeId) : null;
          const cardHint = `card:${t.id}`;
          return (
            <article
              key={t.id}
              className={`ag-card${t.completed ? ' is-done' : ''}${dragId === t.id ? ' is-dragging' : ''}${dropHint === cardHint ? ' is-before' : ''}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                onDragStart(t.id);
              }}
              onDragEnd={onDragEnd}
              onDragOver={(e) => {
                if (!canDrop || !dragId || dragId === t.id) return;
                e.preventDefault();
                e.stopPropagation();
                onHint(cardHint);
              }}
              onDrop={(e) => {
                if (!canDrop || !dragId) return;
                e.preventDefault();
                e.stopPropagation();
                onDrop(i);
              }}
              onClick={() => onOpen(t.id)}
            >
              <div className="ag-card-top">
                <button
                  className={`ag-check ag-check-sm${t.completed ? ' is-done' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(t.id, !t.completed);
                  }}
                  aria-label={t.completed ? 'إعادة فتح' : 'إكمال'}
                >
                  <Icon name="check" size={11} />
                </button>
                <span className={`ag-card-title${t.completed ? ' ag-done-text' : ''}`}>
                  {t.isMilestone && <Icon name="diamond" size={12} className="ag-milestone-ic" />}{' '}
                  {t.title}
                </span>
                <span onClick={(e) => e.stopPropagation()}>
                  <Menu
                    actions={sections
                      .filter((s) => s.id !== t.sectionId)
                      .map((s) => ({
                        key: s.id,
                        label: `انقل إلى «${s.name}»`,
                        icon: 'arrowRightLeft' as const,
                        onSelect: () => onMoveTo(t.id, s.id),
                      }))}
                    label="نقل البطاقة"
                  />
                </span>
              </div>

              {(t.dueAt ||
                assignee ||
                t.subtaskCount > 0 ||
                t.commentCount > 0 ||
                t.fileCount > 0 ||
                t.priority === 'HIGH' ||
                t.serviceName ||
                Object.keys(t.fields).length > 0) && (
                <div className="ag-card-meta">
                  {t.priority === 'HIGH' && !t.completed && (
                    <span className="ag-chip ag-chip-hot">عاجلة</span>
                  )}
                  {t.dueAt && (
                    <span className={`ag-chip${overdue ? ' ag-chip-late' : ''}`}>
                      <Icon name="clock" size={11} />
                      {date(t.dueAt)}
                    </span>
                  )}
                  {t.serviceName && (
                    <span className="ag-chip ag-chip-service" title="تُخصم من باقة العميل عند الإكمال">
                      <span className="num">{t.units}×</span> {t.serviceName}
                    </span>
                  )}
                  {t.subtaskCount > 0 && (
                    <span className="ag-chip num">
                      <Icon name="layers" size={11} />
                      {t.subtaskDone}/{t.subtaskCount}
                    </span>
                  )}
                  {t.commentCount > 0 && (
                    <span className="ag-chip num" title="تعليقات">
                      <Icon name="chat" size={11} />
                      {t.commentCount}
                    </span>
                  )}
                  {t.fileCount > 0 && (
                    <span className="ag-chip num" title="ملفات">
                      <Icon name="paperclip" size={11} />
                      {t.fileCount}
                    </span>
                  )}
                  {fieldDefs
                    .filter((f) => f.kind === 'SELECT' && t.fields[f.id])
                    .map((f) => {
                      const opt = f.options.find((o) => o.id === t.fields[f.id]);
                      return opt ? (
                        <span
                          key={f.id}
                          className="ag-chip"
                          style={{
                            color: opt.color,
                            borderColor: `${opt.color}55`,
                            background: `${opt.color}14`,
                          }}
                          title={f.name}
                        >
                          {opt.label}
                        </span>
                      ) : null;
                    })}
                  {assignee && (
                    <span className="ag-card-avatar" title={assignee.name}>
                      <Avatar name={assignee.name} src={assignee.avatarUrl} size={20} />
                    </span>
                  )}
                </div>
              )}
            </article>
          );
        })}

        {real &&
          (adding ? (
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && title.trim()) {
                  onAdd(title.trim());
                  setTitle('');
                }
                if (e.key === 'Escape') setAdding(false);
              }}
              onBlur={() => {
                if (title.trim()) onAdd(title.trim());
                setTitle('');
                setAdding(false);
              }}
              placeholder="عنوان المهمة ثم Enter"
              autoFocus
            />
          ) : (
            <button className="ag-add-task" onClick={() => setAdding(true)}>
              <Icon name="plus" size={13} />
              مهمة
            </button>
          ))}
      </div>
    </div>
  );
}

/* ==========================================================================
   تعديل المشروع
   ========================================================================== */

function EditProjectModal({
  project,
  onClose,
  onSave,
}: {
  project: AgencyProjectDetail;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const { assignable } = useTeamDirectory();
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? '');
  const [color, setColor] = useState(project.color);
  const [ownerId, setOwnerId] = useState(project.ownerId ?? '');
  const [portfolioId, setPortfolioId] = useState(project.portfolioId ?? '');
  const [goalId, setGoalId] = useState(project.goalId ?? '');
  const [completeOn, setCompleteOn] = useState(project.rules.completeOnSectionId ?? '');
  const [moveOn, setMoveOn] = useState(project.rules.moveOnCompleteSectionId ?? '');

  const portfolios = useQuery({
    queryKey: ['agency', 'portfolios'],
    queryFn: () => api<{ id: string; name: string }[]>('/agency/portfolios'),
  });
  const goals = useQuery({
    queryKey: ['agency', 'goals'],
    queryFn: () => api<{ id: string; title: string; archived: boolean }[]>('/agency/goals'),
  });

  return (
    <Modal title="تعديل المشروع" onClose={onClose} wide>
      <label className="field">
        <span className="label">اسم المشروع</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">
        <span className="label">مالك المشروع</span>
        <select className="select" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
          <option value="">بلا مالك</option>
          {assignable.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="label">الوصف</span>
        <textarea
          className="textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <div className="field">
        <span className="label">اللون</span>
        <div className="ag-swatches">
          {PROJECT_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`ag-swatch${color === c ? ' is-active' : ''}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
              aria-label={`اللون ${c}`}
            />
          ))}
        </div>
      </div>

      <div className="row" style={{ gap: 'var(--sp-3)' }}>
        <label className="field" style={{ flex: 1 }}>
          <span className="label">المحفظة</span>
          <select
            className="select"
            value={portfolioId}
            onChange={(e) => setPortfolioId(e.target.value)}
          >
            <option value="">بلا محفظة</option>
            {(portfolios.data ?? []).map((pf) => (
              <option key={pf.id} value={pf.id}>
                {pf.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span className="label">يخدم هدف</span>
          <select className="select" value={goalId} onChange={(e) => setGoalId(e.target.value)}>
            <option value="">بلا هدف</option>
            {(goals.data ?? [])
              .filter((g) => !g.archived)
              .map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
          </select>
        </label>
      </div>

      {/* قواعد الأتمتة — كقواعد أسانا، تُنفَّذ في الخادم فعلًا */}
      <h4 className="label mt-2 mb-4">
        <Icon name="zap" size={13} /> قواعد الأتمتة
      </h4>
      <div className="row" style={{ gap: 'var(--sp-3)' }}>
        <label className="field" style={{ flex: 1 }}>
          <span className="label">النقل إلى هذا القسم يُكمل المهمة</span>
          <select
            className="select"
            value={completeOn}
            onChange={(e) => setCompleteOn(e.target.value)}
          >
            <option value="">معطّلة</option>
            {project.sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span className="label">إكمال المهمة ينقلها إلى</span>
          <select className="select" value={moveOn} onChange={(e) => setMoveOn(e.target.value)}>
            <option value="">معطّلة</option>
            {project.sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() =>
            onSave({
              name: name.trim(),
              description: description.trim() || null,
              color,
              ownerId: ownerId || null,
              portfolioId: portfolioId || null,
              goalId: goalId || null,
              rules: {
                completeOnSectionId: completeOn || null,
                moveOnCompleteSectionId: moveOn || null,
              },
            })
          }
          disabled={name.trim().length < 2}
        >
          حفظ
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   عرض القائمة — الصفوف مجمَّعةً بالقسم، كقائمة أسانا
   ========================================================================== */

function ListView({
  project,
  bySection,
  byId,
  canManage,
  onOpen,
  onToggle,
  onAdd,
}: {
  project: AgencyProjectDetail;
  bySection: Map<string, AgencyTaskView[]>;
  byId: Map<string, { name: string; avatarUrl: string | null }>;
  canManage: boolean;
  onOpen: (taskId: string) => void;
  onToggle: (taskId: string, completed: boolean) => void;
  onAdd: (sectionId: string, title: string) => void;
}) {
  const selectFields = project.fieldDefs.filter(
    (f) => f.kind === 'SELECT' || f.kind === 'MULTI_SELECT',
  );
  const groups: { section: AgencySectionView; num?: number; tasks: AgencyTaskView[] }[] = [
    ...(bySection.has('none')
      ? [{ section: { id: 'none', name: 'بلا قسم', order: -1 }, tasks: bySection.get('none')! }]
      : []),
    ...project.sections.map((s, i) => ({
      section: s,
      num: i + 1,
      tasks: bySection.get(s.id) ?? [],
    })),
  ];

  return (
    <div className="table-wrap">
      <table className="table ag-list">
        <thead>
          <tr>
            <th style={{ width: 36 }} />
            <th>المهمة</th>
            <th>المسؤول</th>
            <th>الموعد</th>
            <th>الأولوية</th>
            {selectFields.map((f) => (
              <th key={f.id}>{f.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map(({ section, num, tasks }) => (
            <Fragment key={section.id}>
              <tr className="ag-list-section">
                <td colSpan={5 + selectFields.length}>
                  {num != null && <span className="ag-num num">{num}</span>} {section.name}{' '}
                  <span className="tiny mute num">({tasks.length})</span>
                </td>
              </tr>
              {tasks.map((t) => {
                const assignee = t.assigneeId ? byId.get(t.assigneeId) : null;
                const overdue = !t.completed && t.dueAt && new Date(t.dueAt).getTime() < Date.now();
                return (
                  <tr key={t.id} className="ag-list-row" onClick={() => onOpen(t.id)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className={`ag-check ag-check-sm${t.completed ? ' is-done' : ''}`}
                        onClick={() => onToggle(t.id, !t.completed)}
                        aria-label={t.completed ? 'إعادة فتح' : 'إكمال'}
                      >
                        <Icon name="check" size={11} />
                      </button>
                    </td>
                    <td>
                      <span className={t.completed ? 'ag-done-text' : ''}>
                        {t.isMilestone && (
                          <Icon name="diamond" size={12} className="ag-milestone-ic" />
                        )}{' '}
                        {t.title}
                      </span>
                      {t.subtaskCount > 0 && (
                        <span className="tiny mute num">
                          {' '}
                          {t.subtaskDone}/{t.subtaskCount}
                        </span>
                      )}
                    </td>
                    <td>
                      {assignee ? (
                        <span className="row" style={{ gap: 6 }}>
                          <Avatar name={assignee.name} src={assignee.avatarUrl} size={20} />
                          <span className="small">{assignee.name}</span>
                        </span>
                      ) : (
                        <span className="cell-mute">—</span>
                      )}
                    </td>
                    <td className={overdue ? 'ag-late-text num' : 'cell-mute num'}>
                      {t.dueAt ? date(t.dueAt) : '—'}
                    </td>
                    <td>
                      <Badge kind={PRIORITY_MAP[t.priority]?.kind ?? 'neutral'}>
                        {PRIORITY_MAP[t.priority]?.label ?? t.priority}
                      </Badge>
                    </td>
                    {selectFields.map((f) => {
                      const v = t.fields[f.id];
                      const ids = Array.isArray(v) ? v : typeof v === 'string' && v ? [v] : [];
                      const opts = ids
                        .map((idv) => f.options.find((o) => o.id === idv))
                        .filter((o): o is NonNullable<typeof o> => Boolean(o));
                      return (
                        <td key={f.id}>
                          {opts.length ? (
                            <span className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                              {opts.map((opt) => (
                                <span
                                  key={opt.id}
                                  className="ag-chip"
                                  style={{
                                    color: opt.color,
                                    borderColor: opt.color + '55',
                                    background: opt.color + '14',
                                  }}
                                >
                                  {opt.label}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <span className="cell-mute">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {canManage && section.id !== 'none' && (
                <tr className="ag-list-addrow">
                  <td />
                  <td colSpan={4 + selectFields.length}>
                    <input
                      className="ag-inline-input"
                      style={{ width: '100%' }}
                      placeholder="أضف مهمة…"
                      onKeyDown={(e) => {
                        const v = e.currentTarget.value.trim();
                        if (e.key === 'Enter' && v) {
                          onAdd(section.id, v);
                          e.currentTarget.value = '';
                        }
                      }}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ==========================================================================
   حقول المشروع المخصَّصة — إدارتها بنمط أسانا
   ========================================================================== */

/** محرّر خيارات قائمة: تسمية، ولونٌ يُبدَّل بالنقر على نقطته، وحذف. */
function OptionsEditor({
  options,
  onChange,
}: {
  options: AgencyFieldOption[];
  onChange: (next: AgencyFieldOption[]) => void;
}) {
  const [label, setLabel] = useState('');

  const add = () => {
    const v = label.trim();
    if (!v) return;
    onChange([
      ...options,
      {
        id: `o${Date.now().toString(36)}${options.length}`,
        label: v,
        color: PROJECT_COLORS[options.length % PROJECT_COLORS.length]!,
      },
    ]);
    setLabel('');
  };

  const cycleColor = (i: number) => {
    const at = PROJECT_COLORS.indexOf(options[i]!.color as (typeof PROJECT_COLORS)[number]);
    const next = PROJECT_COLORS[(at + 1) % PROJECT_COLORS.length]!;
    onChange(options.map((o, x) => (x === i ? { ...o, color: next } : o)));
  };

  return (
    <div className="ag-opts">
      {options.map((o, i) => (
        <div key={o.id} className="ag-opt-row">
          <button
            type="button"
            className="ag-swatch"
            style={{ background: o.color, width: 20, height: 20 }}
            onClick={() => cycleColor(i)}
            title="بدّل اللون"
            aria-label={`لون ${o.label}`}
          />
          <input
            className="ag-inline-input"
            style={{ flex: 1 }}
            value={o.label}
            onChange={(e) =>
              onChange(options.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))
            }
          />
          <button
            type="button"
            className="btn btn-sm btn-icon btn-ghost"
            onClick={() => onChange(options.filter((_, xi) => xi !== i))}
            aria-label="إزالة الخيار"
          >
            <Icon name="x" size={12} />
          </button>
        </div>
      ))}
      <div className="ag-opt-row">
        <span style={{ width: 20 }} />
        <input
          className="ag-inline-input"
          style={{ flex: 1 }}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add();
          }}
          placeholder="خيارٌ جديد ثم Enter"
        />
        <button type="button" className="btn btn-sm" onClick={add}>
          أضِف
        </button>
      </div>
    </div>
  );
}

/**
 * إدارة حقول المشروع — كنافذة أسانا: النوع بطاقاتٌ تشرح نفسها،
 * والخيارات تُحرَّر بألوانها، والحقول القائمة تُعدَّل في مكانها.
 */
function FieldsModal({
  projectId,
  fields,
  onClose,
  onChanged,
}: {
  projectId: string;
  fields: AgencyFieldView[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<AgencyFieldKind>('TEXT');
  const [options, setOptions] = useState<AgencyFieldOption[]>([]);
  const [editingOpts, setEditingOpts] = useState<string | null>(null);
  const [optsDraft, setOptsDraft] = useState<AgencyFieldOption[]>([]);

  const needsOptions = AGENCY_FIELD_KIND_MAP[kind].hasOptions;

  const create = useMutation({
    mutationFn: () =>
      api(`/agency/projects/${projectId}/fields`, {
        method: 'POST',
        body: {
          name: name.trim(),
          kind,
          options: needsOptions ? options.filter((o) => o.label.trim()) : [],
        },
      }),
    onSuccess: () => {
      setName('');
      setOptions([]);
      setKind('TEXT');
      onChanged();
      toast('أُضيف الحقل');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّرت الإضافة.'),
  });

  const patchField = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api(`/agency/fields/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      setEditingOpts(null);
      onChanged();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/agency/fields/${id}`, { method: 'DELETE' }),
    onSuccess: onChanged,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  return (
    <Modal title="حقول المشروع" onClose={onClose} wide>
      {fields.length === 0 ? (
        <p className="mute small">
          لا حقول بعد. الحقل يظهر صفًّا في بطاقة كل مهمة وعمودًا في القائمة — «المنصة»، «حالة
          الاعتماد»، «الميزانية»…
        </p>
      ) : (
        <div className="ag-fields-list">
          {fields.map((f) => {
            const def = AGENCY_FIELD_KIND_MAP[f.kind];
            return (
              <div key={f.id} className="ag-fields-item">
                <div className="ag-fields-row">
                  <input
                    className="input"
                    defaultValue={f.name}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== f.name) patchField.mutate({ id: f.id, body: { name: v } });
                    }}
                  />
                  <Badge kind={def?.kind}>{def?.label ?? f.kind}</Badge>
                  {def?.hasOptions && (
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        setEditingOpts(editingOpts === f.id ? null : f.id);
                        setOptsDraft(f.options);
                      }}
                    >
                      الخيارات ({f.options.length})
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-icon"
                    onClick={() => {
                      if (window.confirm(`حذف حقل «${f.name}»؟ قيمه على المهام لن تُعرض بعدها.`)) {
                        remove.mutate(f.id);
                      }
                    }}
                    aria-label="حذف الحقل"
                  >
                    <Icon name="trash" size={13} />
                  </button>
                </div>

                {editingOpts === f.id && (
                  <div className="ag-fields-opts">
                    <OptionsEditor options={optsDraft} onChange={setOptsDraft} />
                    <div className="row mt-2" style={{ gap: 6 }}>
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={optsDraft.filter((o) => o.label.trim()).length === 0}
                        onClick={() =>
                          patchField.mutate({
                            id: f.id,
                            body: { options: optsDraft.filter((o) => o.label.trim()) },
                          })
                        }
                      >
                        حفظ الخيارات
                      </button>
                      <button className="btn btn-sm" onClick={() => setEditingOpts(null)}>
                        إلغاء
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <h4 className="label mt-4 mb-4">حقل جديد</h4>

      {/* النوع بطاقاتٌ تشرح نفسها — كنافذة أسانا. */}
      <div className="ag-kind-grid">
        {AGENCY_FIELD_KIND_DEFS.map((d) => (
          <button
            key={d.key}
            type="button"
            className={`ag-kind-card${kind === d.key ? ' is-active' : ''}`}
            onClick={() => setKind(d.key)}
            aria-pressed={kind === d.key}
          >
            <strong>{d.label}</strong>
            <span className="tiny mute">{d.hint}</span>
          </button>
        ))}
      </div>

      <input
        className="input mt-2"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="اسم الحقل — مثل: المنصة"
      />

      {needsOptions && (
        <div className="mt-2">
          <OptionsEditor options={options} onChange={setOptions} />
        </div>
      )}

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() => create.mutate()}
          disabled={
            !name.trim() ||
            (needsOptions && options.filter((o) => o.label.trim()).length === 0) ||
            create.isPending
          }
        >
          أضِف الحقل
        </button>
        <button className="btn" onClick={onClose}>
          إغلاق
        </button>
      </div>
      {needsOptions && options.length === 0 && (
        <p className="tiny mute mt-2">هذا النوع يحتاج خيارًا واحدًا على الأقل.</p>
      )}
    </Modal>
  );
}

/* ==========================================================================
   تقارير المشروع — أرقامٌ سريعة من المهام والحقول
   ========================================================================== */

/**
 * إحصاءات تُحسب في المتصفح من مهام المشروع المجلوبة أصلًا: لا مسار
 * تقارير منفصلًا يتخلّف عن الواقع — ما تراه هنا هو اللوحة نفسها معدودةً.
 *
 * كل حقل «قائمة» يصير رسمًا بألوان خياراته، وكل حقل «رقم» يُجمَع،
 * والمسؤولون يُعدّ لكلٍّ مفتوحُه ومنجزُه.
 */
function ProjectReports({
  project,
  byId,
}: {
  project: AgencyProjectDetail;
  byId: Map<string, { name: string; avatarUrl: string | null }>;
}) {
  const tasks = project.tasks;
  const done = tasks.filter((t) => t.completed).length;
  const overdue = tasks.filter(
    (t) => !t.completed && t.dueAt && new Date(t.dueAt).getTime() < Date.now(),
  ).length;
  const milestones = tasks.filter((t) => t.isMilestone);
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  /* حسب المسؤول */
  const byAssignee = new Map<string, { open: number; done: number }>();
  for (const t of tasks) {
    const key = t.assigneeId ?? '_none';
    const row = byAssignee.get(key) ?? { open: 0, done: 0 };
    if (t.completed) row.done++;
    else row.open++;
    byAssignee.set(key, row);
  }
  const assigneeRows = [...byAssignee.entries()].sort(
    (a, b) => b[1].open + b[1].done - (a[1].open + a[1].done),
  );
  const maxAssignee = Math.max(1, ...assigneeRows.map(([, v]) => v.open + v.done));

  /* حقول القوائم: عدّ لكل خيار */
  const selectFields = project.fieldDefs.filter(
    (f) => f.kind === 'SELECT' || f.kind === 'MULTI_SELECT',
  );
  /* حقول الأرقام: مجموع ومتوسط */
  const numberFields = project.fieldDefs.filter((f) => f.kind === 'NUMBER');

  return (
    <div className="ag-reports">
      {/* الإنجاز */}
      <section className="card">
        <div className="card-body">
          <h3 className="label mb-4">الإنجاز</h3>
          <div className="row" style={{ gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
            <RepStat label="المهام" value={String(tasks.length)} />
            <RepStat label="مكتملة" value={String(done)} />
            <RepStat label="متأخرة" value={String(overdue)} alert={overdue > 0} />
            <RepStat
              label="المعالم"
              value={`${milestones.filter((m) => m.completed).length}/${milestones.length}`}
            />
            <RepStat label="نسبة الإنجاز" value={`${pct}%`} />
          </div>
          <div className="ag-progress mt-2" title={`${done}/${tasks.length}`}>
            <span className="ag-progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </section>

      {/* حسب المسؤول */}
      <section className="card">
        <div className="card-body">
          <h3 className="label mb-4">حسب المسؤول</h3>
          {assigneeRows.length === 0 && <p className="mute small">لا مهام بعد.</p>}
          {assigneeRows.map(([key, v]) => {
            const member = key === '_none' ? null : byId.get(key);
            const total = v.open + v.done;
            return (
              <div key={key} className="ag-rep-row">
                <span className="ag-rep-name">
                  {member ? (
                    <>
                      <Avatar name={member.name} src={member.avatarUrl} size={20} />
                      {member.name}
                    </>
                  ) : key === '_none' ? (
                    'بلا مسؤول'
                  ) : (
                    'مستخدم سابق'
                  )}
                </span>
                <span className="ag-rep-bar">
                  <span
                    className="ag-rep-fill is-done"
                    style={{ width: `${(v.done / maxAssignee) * 100}%` }}
                    title={`مكتملة: ${v.done}`}
                  />
                  <span
                    className="ag-rep-fill"
                    style={{ width: `${(v.open / maxAssignee) * 100}%` }}
                    title={`مفتوحة: ${v.open}`}
                  />
                </span>
                <span className="tiny mute num">
                  {v.done}/{total}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* حقول القوائم */}
      {selectFields.map((f) => {
        const counts = new Map<string, number>();
        let empty = 0;
        for (const t of tasks) {
          const v = t.fields[f.id];
          const ids = Array.isArray(v) ? v : typeof v === 'string' && v ? [v] : [];
          if (ids.length === 0) empty++;
          for (const idv of ids) counts.set(idv, (counts.get(idv) ?? 0) + 1);
        }
        const max = Math.max(1, ...f.options.map((o) => counts.get(o.id) ?? 0), empty);
        return (
          <section key={f.id} className="card">
            <div className="card-body">
              <h3 className="label mb-4">حسب {f.name}</h3>
              {f.options.map((o) => {
                const n = counts.get(o.id) ?? 0;
                return (
                  <div key={o.id} className="ag-rep-row">
                    <span className="ag-rep-name">
                      <span className="ag-dot" style={{ background: o.color }} />
                      {o.label}
                    </span>
                    <span className="ag-rep-bar">
                      <span
                        className="ag-rep-fill"
                        style={{ width: `${(n / max) * 100}%`, background: o.color }}
                      />
                    </span>
                    <span className="tiny mute num">{n}</span>
                  </div>
                );
              })}
              {empty > 0 && (
                <div className="ag-rep-row">
                  <span className="ag-rep-name mute">بلا قيمة</span>
                  <span className="ag-rep-bar">
                    <span className="ag-rep-fill is-empty" style={{ width: `${(empty / max) * 100}%` }} />
                  </span>
                  <span className="tiny mute num">{empty}</span>
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* حقول الأرقام */}
      {numberFields.length > 0 && (
        <section className="card">
          <div className="card-body">
            <h3 className="label mb-4">مجاميع الأرقام</h3>
            {numberFields.map((f) => {
              const values = tasks
                .map((t) => t.fields[f.id])
                .filter((v): v is number => typeof v === 'number');
              const sum = values.reduce((a, b) => a + b, 0);
              const avg = values.length ? sum / values.length : 0;
              return (
                <div key={f.id} className="ag-rep-row">
                  <span className="ag-rep-name">{f.name}</span>
                  <span className="tiny mute num" style={{ marginInlineStart: 'auto' }}>
                    المجموع {Math.round(sum * 100) / 100} · المتوسط{' '}
                    {Math.round(avg * 100) / 100} · على {values.length} مهمة
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {selectFields.length === 0 && numberFields.length === 0 && (
        <p className="tiny mute">
          أضِف حقولًا من قائمة المشروع ← «حقول المشروع» لتظهر تقاريرها هنا تلقائيًّا.
        </p>
      )}
    </div>
  );
}

function RepStat({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <span className="ag-rep-stat">
      <strong className={`num${alert ? ' ag-late-text' : ''}`}>{value}</strong>
      <span className="tiny mute">{label}</span>
    </span>
  );
}

/* ==========================================================================
   نظرة عامة — كصفحة Overview في أسانا
   ========================================================================== */

/**
 * وصف المشروع وحالته المعلنة وفريقه — ما يقرؤه الداخل الجديد أولًا.
 *
 * الحالة يعلنها المدير («على المسار/في خطر/متعثّر») ولا تُحسب آليًّا:
 * هي حكمُ من يقود المشروع لا ناتجَ معادلة، والتقدّم المحسوب بجوارها
 * يكشف إن تباعدا.
 */
function OverviewTab({
  project,
  byId,
  canManage,
  onSave,
}: {
  project: AgencyProjectDetail;
  byId: Map<string, { name: string; avatarUrl: string | null }>;
  canManage: boolean;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [desc, setDesc] = useState<string | null>(null);
  const [clientPreview, setClientPreview] = useState(false);

  const done = project.tasks.filter((t) => t.completed).length;
  const pct = project.tasks.length ? Math.round((done / project.tasks.length) * 100) : 0;

  /* فريق المشروع: المالك + كل من أُسندت إليه مهمة. */
  const memberIds = [
    ...new Set(
      [project.ownerId, ...project.tasks.map((t) => t.assigneeId)].filter(
        (v): v is string => Boolean(v),
      ),
    ),
  ];

  return (
    <div className="ag-overview">
      <div className="ag-overview-main">
        <section className="card">
          <div className="card-body">
            <h3 className="label mb-4">وصف المشروع</h3>
            {canManage ? (
              <>
                <textarea
                  className="textarea"
                  rows={5}
                  value={desc ?? project.description ?? ''}
                  onChange={(e) => setDesc(e.target.value)}
                  placeholder="ما هذا المشروع؟ ما أهدافه؟ وما قواعد العمل فيه؟"
                />
                {desc !== null && desc !== (project.description ?? '') && (
                  <div className="row mt-2" style={{ gap: 6 }}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => {
                        onSave({ description: desc.trim() || null });
                        setDesc(null);
                      }}
                    >
                      حفظ الوصف
                    </button>
                    <button className="btn btn-sm" onClick={() => setDesc(null)}>
                      تراجع
                    </button>
                  </div>
                )}
              </>
            ) : project.description ? (
              <p className="small" style={{ whiteSpace: 'pre-wrap' }}>
                {project.description}
              </p>
            ) : (
              <p className="mute small">لا وصف بعد.</p>
            )}
          </div>
        </section>

        <section className="card">
          <div className="card-body">
            <h3 className="label mb-4">فريق المشروع</h3>
            {memberIds.length === 0 && <p className="mute small">لا مُسنَد إليهم بعد.</p>}
            <div className="row" style={{ gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
              {memberIds.map((uid) => {
                const m = byId.get(uid);
                if (!m) return null;
                return (
                  <span key={uid} className="ag-ctx-chip">
                    <Avatar name={m.name} src={m.avatarUrl} size={22} />
                    {m.name}
                    {uid === project.ownerId && <span className="tiny mute"> · المالك</span>}
                  </span>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      <div className="ag-overview-side">
        <section className="card">
          <div className="card-body">
            <h3 className="label mb-4">ما حالة المشروع؟</h3>
            <div className="ag-health-row">
              {PROJECT_HEALTH_DEFS.map((h) => {
                const active = project.health === h.key;
                return (
                  <button
                    key={h.key}
                    className={`ag-health ag-health-${h.kind}${active ? ' is-active' : ''}`}
                    disabled={!canManage}
                    onClick={() => canManage && onSave({ health: active ? null : h.key })}
                    aria-pressed={active}
                  >
                    <span className="ag-health-dot" />
                    {h.label}
                  </button>
                );
              })}
            </div>
            {!canManage && !project.health && (
              <p className="tiny mute mt-2">لم يعلن المدير حالةً بعد.</p>
            )}

            {/* رأي العميل في المشروع كلّه — نجومُه ونصُّه للجميع، والطلب للمدير. */}
            {project.accountId && (
              <>
                <h3 className="label mb-2 mt-4">تقييم العميل للمشروع</h3>
                <EntityRating kind="PROJECT" entityId={project.id} canRequest={canManage} />
              </>
            )}

            <h3 className="label mb-4 mt-4">التقدّم</h3>
            <div className="ag-progress" title={`${done}/${project.tasks.length}`}>
              <span className="ag-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <p className="tiny mute num mt-2">
              {done}/{project.tasks.length} مهمة · {pct}% · أُنشئ {date(project.createdAt)}
            </p>

            {project.accountName && (
              <>
                <h3 className="label mb-4 mt-4">العميل</h3>
                <button className="ag-ctx-chip" onClick={() => setClientPreview(true)}>
                  <Icon name="building" size={13} />
                  {project.accountName}
                </button>
              </>
            )}
          </div>
        </section>
      </div>

      {clientPreview && project.accountId && (
        <ClientPreviewModal
          accountId={project.accountId}
          onClose={() => setClientPreview(false)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   تقويم المشروع — شهرٌ تتوزّع فيه المهام بمواعيدها
   ========================================================================== */

function CalendarTab({
  project,
  onOpen,
}: {
  project: AgencyProjectDetail;
  onOpen: (taskId: string) => void;
}) {
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  /*
   * أنشطة CRM مع عميل المشروع — اجتماعاتٌ ومكالماتٌ ورسائل تُعرض بجوار
   * المهام: إدارة الشغل مع العميل تقويمٌ واحد لا شاشتان تُقارنان بالعين.
   */
  const activities = useQuery({
    queryKey: ['agency', 'cal-activities', project.accountId],
    queryFn: () =>
      api<{
        items: Array<{
          id: string;
          type: string;
          subject: string;
          dueAt: string | null;
          completedAt: string | null;
          status: string;
        }>;
      }>(`/crm/activities${qs({ accountId: project.accountId!, pageSize: 100 })}`),
    enabled: Boolean(project.accountId),
  });

  const ACT_ICON: Record<string, IconName> = {
    MEETING: 'video',
    CALL: 'phone',
    MAIL: 'mail',
    TASK: 'clipboard',
    NOTE: 'note',
  };
  const byActDay = new Map<string, NonNullable<typeof activities.data>['items']>();
  for (const a of activities.data?.items ?? []) {
    /* المخطَّط بموعده، والمنجَز بلا موعدٍ بيوم إتمامه — كلاهما تاريخُ تعامل. */
    const when = a.dueAt ?? a.completedAt;
    if (!when) continue;
    const key = when.slice(0, 10);
    byActDay.set(key, [...(byActDay.get(key) ?? []), a]);
  }

  const monthLabel = anchor.toLocaleDateString('ar-EG', { month: 'long', year: 'numeric' });

  /* شبكة ستة أسابيع تبدأ سبت الأسبوع الأول — السبت أول الأسبوع في مصر. */
  const gridStart = new Date(anchor);
  const back = (gridStart.getDay() + 1) % 7; // السبت = 6 → صفر إزاحة
  gridStart.setDate(gridStart.getDate() - back);

  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }

  const byDay = new Map<string, AgencyTaskView[]>();
  for (const t of project.tasks) {
    if (!t.dueAt) continue;
    const key = t.dueAt.slice(0, 10);
    byDay.set(key, [...(byDay.get(key) ?? []), t]);
  }
  const noDate = project.tasks.filter((t) => !t.dueAt && !t.completed).length;
  const todayKey = new Date().toISOString().slice(0, 10);

  const shift = (n: number) => {
    const d = new Date(anchor);
    d.setMonth(d.getMonth() + n);
    setAnchor(d);
  };

  return (
    <div className="ag-cal">
      <div className="row mb-4" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
        <button className="btn btn-sm btn-icon" onClick={() => shift(1)} aria-label="الشهر التالي">
          <Icon name="chevronUp" size={14} />
        </button>
        <strong>{monthLabel}</strong>
        <button
          className="btn btn-sm btn-icon"
          onClick={() => shift(-1)}
          aria-label="الشهر السابق"
        >
          <Icon name="chevronUp" size={14} className="ag-flip" />
        </button>
        <button
          className="btn btn-sm"
          onClick={() => {
            const d = new Date();
            d.setDate(1);
            d.setHours(0, 0, 0, 0);
            setAnchor(d);
          }}
        >
          اليوم
        </button>
        {noDate > 0 && (
          <span className="tiny mute" style={{ marginInlineStart: 'auto' }}>
            {noDate} مهمة مفتوحة بلا موعد لا تظهر هنا
          </span>
        )}
      </div>

      <div className="ag-cal-grid">
        {['السبت', 'الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة'].map((d) => (
          <div key={d} className="ag-cal-head">
            {d}
          </div>
        ))}
        {days.map((d) => {
          const key = d.toISOString().slice(0, 10);
          const inMonth = d.getMonth() === anchor.getMonth();
          const tasks = byDay.get(key) ?? [];
          return (
            <div
              key={key}
              className={`ag-cal-day${inMonth ? '' : ' is-out'}${key === todayKey ? ' is-today' : ''}`}
            >
              <span className="ag-cal-date num">{d.getDate()}</span>
              {tasks.slice(0, 3).map((t) => (
                <button
                  key={t.id}
                  className={`ag-cal-task${t.completed ? ' is-done' : ''}`}
                  onClick={() => onOpen(t.id)}
                  title={t.title}
                >
                  {t.isMilestone && <Icon name="diamond" size={10} className="ag-milestone-ic" />}
                  {t.title}
                </button>
              ))}
              {tasks.length > 3 && (
                <span className="tiny mute num">+{tasks.length - 3} أخرى</span>
              )}
              {(byActDay.get(key) ?? []).slice(0, 2).map((a) => (
                <span
                  key={a.id}
                  className={`ag-cal-act${a.status === 'DONE' ? ' is-done' : ''}`}
                  title={a.subject}
                >
                  <Icon name={ACT_ICON[a.type] ?? 'clipboard'} size={10} />
                  {a.subject}
                </span>
              ))}
              {(byActDay.get(key)?.length ?? 0) > 2 && (
                <span className="tiny mute num">+{byActDay.get(key)!.length - 2} نشاط</span>
              )}
            </div>
          );
        })}
      </div>

      {project.accountId && (
        <p className="tiny mute mt-2">
          <Icon name="video" size={11} /> الرقاقات المخطَّطة أنشطةُ CRM مع العميل (اجتماعات
          ومكالمات ورسائل) — تُدار من «الأنشطة» في إدارة العملاء.
        </p>
      )}
    </div>
  );
}

/* ==========================================================================
   الخطّ الزمنيّ — أشرطة مددٍ على أسابيع، كـTimeline أسانا
   ========================================================================== */

/**
 * كل مهمةٍ شريطٌ من بدايتها إلى استحقاقها، والمعلم معيّن، وما له موعدٌ
 * واحد يُرسم يومًا واحدًا. المدد تُعدَّل من بطاقة المهمة («المدة») —
 * السحب لتغيير المدد لم يُبنَ بعد فلا نوحي بغيره.
 */
function TimelineTab({
  project,
  onOpen,
}: {
  project: AgencyProjectDetail;
  onOpen: (taskId: string) => void;
}) {
  const dated = project.tasks.filter((t) => t.startAt || t.dueAt);
  const undated = project.tasks.length - dated.length;

  if (dated.length === 0) {
    return (
      <p className="mute small">
        لا مهام مؤرَّخة بعد — حدّد «المدة» (بداية واستحقاق) من بطاقة المهمة ليرسمها الخطّ
        الزمنيّ.
      </p>
    );
  }

  /* مدى الرسم: من أول تاريخٍ إلى آخره، موسَّعًا لحدود الأسابيع (السبت). */
  const stamps = dated.flatMap((t) =>
    [t.startAt, t.dueAt].filter((v): v is string => Boolean(v)).map((v) => new Date(v).getTime()),
  );
  const min = new Date(Math.min(...stamps));
  const max = new Date(Math.max(...stamps));
  const start = new Date(min);
  start.setDate(start.getDate() - ((start.getDay() + 1) % 7)); // إلى السبت
  start.setHours(0, 0, 0, 0);
  const end = new Date(max);
  end.setDate(end.getDate() + (6 - ((end.getDay() + 1) % 7)) + 1);
  end.setHours(0, 0, 0, 0);
  const totalMs = Math.max(end.getTime() - start.getTime(), 86_400_000);

  const weeks: Date[] = [];
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 7)) weeks.push(new Date(d));

  const pct = (ms: number) => Math.min(100, Math.max(0, ((ms - start.getTime()) / totalMs) * 100));
  const now = Date.now();
  const todayPct = now >= start.getTime() && now <= end.getTime() ? pct(now) : null;

  const groups: { section: AgencySectionView; num?: number; tasks: AgencyTaskView[] }[] = [
    ...project.sections.map((s, i) => ({
      section: s,
      num: i + 1,
      tasks: dated.filter((t) => t.sectionId === s.id),
    })),
    ...(dated.some((t) => !t.sectionId)
      ? [
          {
            section: { id: 'none', name: 'بلا قسم', order: -1 },
            tasks: dated.filter((t) => !t.sectionId),
          },
        ]
      : []),
  ];

  return (
    <div className="ag-tl">
      {undated > 0 && (
        <p className="tiny mute mb-4">{undated} مهمة بلا تاريخٍ لا تظهر على الخطّ.</p>
      )}

      {/* رأس الأسابيع */}
      <div className="ag-tl-row ag-tl-head">
        <div className="ag-tl-label" />
        <div className="ag-tl-track">
          {weeks.map((w) => (
            <span
              key={w.toISOString()}
              className="ag-tl-week"
              style={{ insetInlineStart: `${pct(w.getTime())}%` }}
            >
              {w.toLocaleDateString('ar-EG', { day: 'numeric', month: 'short' })}
            </span>
          ))}
          {todayPct != null && (
            <span className="ag-tl-today" style={{ insetInlineStart: `${todayPct}%` }} />
          )}
        </div>
      </div>

      {groups.map(({ section, num, tasks }) => (
        <Fragment key={section.id}>
          <div className="ag-tl-section">
            {num != null && <span className="ag-num num">{num}</span>} {section.name}
          </div>
          {tasks.map((t) => {
            const s = t.startAt ? new Date(t.startAt).getTime() : new Date(t.dueAt!).getTime();
            const e = t.dueAt
              ? new Date(t.dueAt).getTime() + 86_400_000
              : s + 86_400_000;
            const left = pct(Math.min(s, e));
            const width = Math.max(pct(Math.max(s, e)) - left, 0.8);
            const single = !t.startAt || !t.dueAt || t.startAt.slice(0, 10) === t.dueAt.slice(0, 10);
            return (
              <div key={t.id} className="ag-tl-row">
                <button className="ag-tl-label" onClick={() => onOpen(t.id)} title={t.title}>
                  {t.isMilestone && <Icon name="diamond" size={11} className="ag-milestone-ic" />}
                  <span className={t.completed ? 'ag-done-text' : ''}>{t.title}</span>
                </button>
                <div className="ag-tl-track">
                  {todayPct != null && (
                    <span className="ag-tl-today" style={{ insetInlineStart: `${todayPct}%` }} />
                  )}
                  {t.isMilestone && single ? (
                    <button
                      className={`ag-tl-diamond${t.completed ? ' is-done' : ''}`}
                      style={{ insetInlineStart: `${left}%` }}
                      onClick={() => onOpen(t.id)}
                      title={t.title}
                      aria-label={t.title}
                    />
                  ) : (
                    <button
                      className={`ag-tl-bar${t.completed ? ' is-done' : ''}`}
                      style={{
                        insetInlineStart: `${left}%`,
                        width: `${width}%`,
                        background: t.completed ? undefined : project.color,
                      }}
                      onClick={() => onOpen(t.id)}
                      title={t.title}
                      aria-label={t.title}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}
